// frontend/src/components/ApplicationStatus.js

import React, { useState, useEffect, useMemo } from 'react';
import { getApplicationStatusDepartments, getApplicationStatusTotals } from '../services/api';
import '../styles/ApplicationStatusNew.css';
import { motion } from 'framer-motion';
import { 
  RefreshCw, 
  LayoutList, 
  Info, 
  Building2, 
  ChevronRight,
  Activity,
  HelpCircle
} from 'lucide-react';
import OnboardingTour from './OnboardingTour';

// チュートリアルステップ定義
const TOUR_STEPS = [
  {
    target: '[data-tour="app-status-header"]',
    title: 'リアルタイム応募状況',
    content: 'このページでは、各病院・診療科の現在の応募状況や空き枠をリアルタイムで確認できます。',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="app-status-sidebar"]',
    title: '病院一覧（目次）',
    content: '左側のサイドバーに全ての病院が一覧表示されています。クリックすると、その病院の情報まで自動スクロールします。',
    placement: 'right',
  },
  {
    target: '[data-tour="app-status-mode-toggle"]',
    title: '表示モード切替',
    content: '「第1希望数/定員」と「残枠」で表示を切り替えられます。自分のニーズに合わせて確認できます。',
    placement: 'bottom',
  },
  {
    target: '[data-tour="app-status-refresh"]',
    title: '更新ボタン',
    content: '最新のデータに更新したい場合は、このボタンをクリックしてください。',
    placement: 'left',
  },
  {
    target: '[data-tour="app-status-table"]',
    title: '応募状況テーブル',
    content: '各病院の科ごと・タームごとの応募状況が表で表示されます。色分けで混雑状況が一目でわかります。',
    placement: 'top',
  },
];

const TERMS = [1, 2, 3, 4, 5, 6, 7];
const DISPLAY_MODE_APPLICANTS = 'applicants';
const DISPLAY_MODE_VACANCY = 'vacancy';
const DISPLAY_MODE_REMAINING = 'remaining';

// Helper to ensure non-negative capacity display
const formatCapacity = (val) => val;

// 一次抽選（first）の表
const PrimaryTable = ({ hospitalName, hospitalData, displayMode }) => {
  return (
    <motion.div 
      id={`hospital-${hospitalName}`} 
      className="hospital-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="hospital-header">
        <h3 className="hospital-name">
          <Building2 size={20} className="text-blue-600" />
          {hospitalName}
        </h3>
      </div>
      <div className="table-container">
        <table className="status-table">
          <thead>
            <tr>
              <th className="department-header">科</th>
              {TERMS.map(termId => (
                <th key={`header-term-${termId}`} className="term-header">
                  {`ターム${termId}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(hospitalData.departments).map(([departmentName, termsData]) => (
              <tr key={`department-${hospitalName}-${departmentName}`}>
                <td className="department-name">{departmentName}</td>
                {TERMS.map(termId => {
                  const termData = termsData[termId];
                  const key = `term-${hospitalName}-${departmentName}-${termId}`;
                  if (!termData) {
                    return <td key={key} className="term-data">-</td>;
                  }
                  const { capacity, displayData } = termData;
                  const displayCap = formatCapacity(capacity);

                  let displayText = '';
                  let value = 0;

                  if (displayMode === DISPLAY_MODE_APPLICANTS) {
                    value = displayData.applicants;
                    displayText = `${value}/${displayCap}`;
                  } else {
                    // VACANCY
                    value = displayData.vacancy;
                    displayText = `${value}`;
                  }

                  let cellClass = '';
                  if (displayCap === 0) {
                    cellClass = 'capacity-zero';
                  } else if (displayMode === DISPLAY_MODE_APPLICANTS) {
                    if (value > displayCap) {
                      cellClass = 'applicants-exceed-capacity';
                    } else if (value > displayCap / 2) {
                      cellClass = 'applicants-half-capacity';
                    } else {
                      cellClass = 'applicants-below-half-capacity';
                    }
                  } else {
                    // VACANCY
                    if (value >= 3) {
                      cellClass = 'vacancy-high';
                    } else if (value >= 1) {
                      cellClass = 'vacancy-medium';
                    } else {
                      cellClass = 'vacancy-low';
                    }
                  }

                  return (
                    <td key={key} className={cellClass}>
                      <span className="cell-value">{displayText}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
            {/* 合計行 */}
            <tr className="total-row">
              <td className="total-label">合計</td>
              {TERMS.map(termId => {
                const termData = hospitalData.totalRow[termId];
                const key = `total-${hospitalName}-term-${termId}`;
                if (!termData) {
                  return <td key={key} className="term-data">-</td>;
                }

                const { stay_capacity, total_capacity, displayData } = termData;
                const displayStayCap = formatCapacity(stay_capacity);
                const displayTotalCap = formatCapacity(total_capacity);
                
                const firstPrefSum = displayData.first_preference_count_sum || 0;

                let stayDisplay = '';
                let totalDisplay = '';
                if (displayMode === DISPLAY_MODE_APPLICANTS) {
                  stayDisplay = `${firstPrefSum}/${displayStayCap}`;
                  totalDisplay = `${firstPrefSum}/${displayTotalCap}`;
                } else {
                  const stayValue = displayData.stay_vacancy;
                  const overallValue = displayData.total_vacancy;
                  stayDisplay = `${stayValue}`;
                  totalDisplay = `${overallValue}`;
                }

                let cellClass = '';
                if (displayTotalCap === 0) {
                  cellClass = 'capacity-zero';
                } else if (displayMode === DISPLAY_MODE_APPLICANTS) {
                  cellClass = 'applicants-below-half-capacity';
                } else {
                  const overallValue = displayData.total_vacancy;
                  if (overallValue >= 3) {
                    cellClass = 'vacancy-high';
                  } else if (overallValue >= 1) {
                    cellClass = 'vacancy-medium';
                  } else {
                    cellClass = 'vacancy-low';
                  }
                }

                return (
                  <td key={key} className={cellClass}>
                    <div className="flex flex-col text-xs">
                      <span>(宿泊) {stayDisplay}</span>
                      <span>(総) {totalDisplay}</span>
                    </div>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

// 病院交換申請（exchange）の表
const ExchangeTable = ({ hospitalName, hospitalData, displayMode }) => {
  return (
    <motion.div 
      id={`hospital-${hospitalName}`} 
      className="hospital-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="hospital-header">
        <h3 className="hospital-name">
          <Building2 size={20} className="text-blue-600" />
          {hospitalName}
        </h3>
      </div>
      <div className="table-container">
        <table className="status-table">
          <thead>
            <tr>
              <th className="department-header">科</th>
              {TERMS.map(termId => (
                <th key={`header-term-${termId}`} className="term-header">
                  {`ターム${termId}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(hospitalData.departments).map(([departmentName, termsData]) => (
              <tr key={`department-${hospitalName}-${departmentName}`}>
                <td className="department-name">{departmentName}</td>
                {TERMS.map(termId => {
                  const termData = termsData[termId];
                  const key = `term-${hospitalName}-${departmentName}-${termId}`;
                  if (!termData) {
                    return <td key={key} className="term-data">-</td>;
                  }
                  const { capacity, displayData } = termData;
                  const displayCap = formatCapacity(capacity);

                  let displayText = '';
                  let value = 0;

                  if (displayMode === DISPLAY_MODE_REMAINING) {
                    value = displayData.remaining;
                    displayText = `${value}/${displayCap}`;
                  } else {
                    value = displayData.vacancy;
                    displayText = `${value}`;
                  }

                  let cellClass = '';
                  if (displayCap === 0) {
                    cellClass = 'capacity-zero';
                  } else if (displayMode === DISPLAY_MODE_REMAINING) {
                    if (value > displayCap / 2) {
                      cellClass = 'remaining-high';
                    } else if (value >= 1) {
                      cellClass = 'remaining-medium';
                    } else {
                      cellClass = 'remaining-low';
                    }
                  } else {
                    if (value >= 3) {
                      cellClass = 'vacancy-high';
                    } else if (value >= 1) {
                      cellClass = 'vacancy-medium';
                    } else {
                      cellClass = 'vacancy-low';
                    }
                  }

                  return (
                    <td key={key} className={cellClass}>
                      <span className="cell-value">{displayText}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
            {/* 合計行 */}
            <tr className="total-row">
              <td className="total-label">合計</td>
              {TERMS.map(termId => {
                const termData = hospitalData.totalRow[termId];
                const key = `total-${hospitalName}-term-${termId}`;
                if (!termData) {
                  return <td key={key} className="term-data">-</td>;
                }
                const { stay_capacity, total_capacity, displayData } = termData;
                const displayStayCap = formatCapacity(stay_capacity);
                const displayTotalCap = formatCapacity(total_capacity);

                let stayDisplay = '';
                let totalDisplay = '';

                if (displayMode === DISPLAY_MODE_REMAINING) {
                  const stayRemain = displayData.stay_vacancy;
                  const totalRemain = displayData.total_vacancy;
                  stayDisplay = `${stayRemain}/${displayStayCap}`;
                  totalDisplay = `${totalRemain}/${displayTotalCap}`;
                } else {
                  const stayVal = displayData.stay_vacancy;
                  const totalVal = displayData.total_vacancy;
                  stayDisplay = `${stayVal}`;
                  totalDisplay = `${totalVal}`;
                }

                let cellClass = '';
                if (displayTotalCap === 0) {
                  cellClass = 'capacity-zero';
                } else if (displayMode === DISPLAY_MODE_REMAINING) {
                  const totalRemain = displayData.total_vacancy;
                  if (totalRemain > displayTotalCap / 2) {
                    cellClass = 'remaining-high';
                  } else if (totalRemain >= 1) {
                    cellClass = 'remaining-medium';
                  } else {
                    cellClass = 'remaining-low';
                  }
                } else {
                  const vacVal = displayData.total_vacancy;
                  if (vacVal >= 3) {
                    cellClass = 'vacancy-high';
                  } else if (vacVal >= 1) {
                    cellClass = 'vacancy-medium';
                  } else {
                    cellClass = 'vacancy-low';
                  }
                }

                return (
                  <td key={key} className={cellClass}>
                    <div className="flex flex-col text-xs">
                      <span>(宿泊) {stayDisplay}</span>
                      <span>(総) {totalDisplay}</span>
                    </div>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

const ApplicationStatus = () => {
  const [departmentsData, setDepartmentsData] = useState([]);
  const [totalsData, setTotalsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [displayMode, setDisplayMode] = useState(DISPLAY_MODE_APPLICANTS);
  const [isExchangeRound, setIsExchangeRound] = useState(false);
  const [showTour, setShowTour] = useState(false);

  // ツアー完了時のハンドラー
  const handleTourComplete = () => {
    setShowTour(false);
  };

  // ガイドボタンクリック時のハンドラー
  const handleGuideClick = () => {
    setShowTour(true);
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [deptData, totData] = await Promise.all([
        getApplicationStatusDepartments(),
        getApplicationStatusTotals()
      ]);
      setDepartmentsData(deptData);
      setTotalsData(totData);
      setError(null);

      setIsExchangeRound(totData[0]?.current_round === 'exchange');
    } catch (err) {
      console.error('データ取得に失敗しました:', err);
      setError('データ取得に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 右側目次のクリックでスクロールする
  const handleTOCClick = (hospitalName) => {
    const anchorId = `hospital-${hospitalName}`;
    const elem = document.getElementById(anchorId);
    if (elem) {
      const headerOffset = 80;
      const elementPosition = elem.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  const transformData = useMemo(() => {
    return (departments, totals) => {
      const transformed = {};

      departments.forEach(item => {
        const { institution_name, department_name, term_id, capacity, vacancy, first_preference_count } = item;
        if (!transformed[institution_name]) {
          transformed[institution_name] = { departments: {}, totalRow: {} };
        }
        if (!transformed[institution_name].departments[department_name]) {
          transformed[institution_name].departments[department_name] = {};
        }

        transformed[institution_name].departments[department_name][term_id] = {
          capacity: capacity,
          displayData: {
            applicants: first_preference_count,
            vacancy,
            remaining: vacancy,
          }
        };
      });

      totals.forEach(total => {
        const {
          institution_name, term_id,
          stay_capacity, total_capacity,
          stay_vacancy, total_vacancy,
          first_preference_count_sum,
          existing_assignment_count_sum
        } = total;

        if (!transformed[institution_name]) {
          transformed[institution_name] = { departments: {}, totalRow: {} };
        }

        transformed[institution_name].totalRow[term_id] = {
          stay_capacity: (stay_capacity || 0) - (existing_assignment_count_sum || 0),
          total_capacity: (total_capacity || 0) - (existing_assignment_count_sum || 0),
          displayData: {
            stay_vacancy,
            total_vacancy,
            remaining: total_vacancy,
            vacancy: total_vacancy,
            first_preference_count_sum,
          }
        };
      });

      return transformed;
    };
  }, []);

  const statusData = useMemo(() => {
    if (!departmentsData.length || !totalsData.length) {
      return {};
    }
    return transformData(departmentsData, totalsData);
  }, [departmentsData, totalsData, transformData]);

  const hospitalNames = useMemo(() => Object.keys(statusData || {}), [statusData]);

  const renderDisplayModeButtons = () => {
    if (isExchangeRound) {
      return (
        <div className="mode-toggle">
          <button
            onClick={() => setDisplayMode(DISPLAY_MODE_REMAINING)}
            className={`mode-btn ${displayMode === DISPLAY_MODE_REMAINING ? 'active' : ''}`}
          >
            残枠/定員
          </button>
          <button
            onClick={() => setDisplayMode(DISPLAY_MODE_VACANCY)}
            className={`mode-btn ${displayMode === DISPLAY_MODE_VACANCY ? 'active' : ''}`}
          >
            残枠
          </button>
        </div>
      );
    } else {
      return (
        <div className="mode-toggle">
          <button
            onClick={() => setDisplayMode(DISPLAY_MODE_APPLICANTS)}
            className={`mode-btn ${displayMode === DISPLAY_MODE_APPLICANTS ? 'active' : ''}`}
          >
            第1希望数/定員
          </button>
          <button
            onClick={() => setDisplayMode(DISPLAY_MODE_VACANCY)}
            className={`mode-btn ${displayMode === DISPLAY_MODE_VACANCY ? 'active' : ''}`}
          >
            残枠
          </button>
        </div>
      );
    }
  };

  return (
    <div className="application-status-page">
      <OnboardingTour
        steps={TOUR_STEPS}
        tourId="application-status-tour"
        autoStart={showTour}
        onComplete={handleTourComplete}
      />
      <div className="status-container">
        {/* サイドバー (目次) */}
        <aside className="status-sidebar" data-tour="app-status-sidebar">
          <h3 className="sidebar-title">
            <LayoutList size={20} />
            病院一覧
          </h3>
          <ul className="toc-list">
            {hospitalNames.map(hname => (
              <li key={hname}>
                <button
                  onClick={() => handleTOCClick(hname)}
                  className="toc-link"
                >
                  <ChevronRight size={16} className="inline mr-1" />
                  {hname}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* メインコンテンツ */}
        <main className="status-main">
          <div className="page-header" data-tour="app-status-header">
            <div className="page-header-content">
              <h2 className="page-title">
                <Activity className="text-blue-600" size={32} />
                リアルタイム応募状況
              </h2>
              <p className="page-description">
                各病院・診療科の現在の応募状況や空き枠を確認できます。
              </p>
            </div>
            <button
              type="button"
              className="guide-btn"
              onClick={handleGuideClick}
              title="操作ガイドを表示"
            >
              <HelpCircle size={18} />
              ガイド
            </button>
          </div>

          <div className="controls-card">
            <div className="control-group" data-tour="app-status-mode-toggle">
              <span className="text-sm font-medium text-gray-600">表示モード:</span>
              {renderDisplayModeButtons()}
            </div>
            <button onClick={fetchData} className="refresh-btn" data-tour="app-status-refresh">
              <RefreshCw size={16} />
              更新
            </button>
          </div>

          {isLoading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>データを読み込んでいます...</p>
            </div>
          ) : error ? (
            <div className="loading-container">
              <p className="text-red-500">{error}</p>
              <button onClick={fetchData} className="refresh-btn mt-4">
                再試行
              </button>
            </div>
          ) : (
            <>
              <div data-tour="app-status-table">
                {hospitalNames.map((hospitalName, index) => {
                  const hospitalData = statusData[hospitalName];
                  if (!hospitalData) return null;

                  return isExchangeRound ? (
                    <ExchangeTable
                      key={hospitalName}
                      hospitalName={hospitalName}
                      hospitalData={hospitalData}
                      displayMode={displayMode}
                    />
                  ) : (
                    <PrimaryTable
                      key={hospitalName}
                      hospitalName={hospitalName}
                      hospitalData={hospitalData}
                      displayMode={displayMode}
                    />
                  );
                })}
              </div>
              
              {/* 凡例 */}
              <div className="legend-card" data-tour="app-status-legend">
                <span className="legend-title flex items-center gap-2">
                  <Info size={18} />
                  凡例:
                </span>
                <div className="legend-item">
                  <span className="legend-color applicants-below-half-capacity" />
                  <span>余裕あり (-50%)</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color applicants-half-capacity" />
                  <span>混雑 (50%-)</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color applicants-exceed-capacity" />
                  <span>超過 (100%-)</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color capacity-zero" />
                  <span>定員なし</span>
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default React.memo(ApplicationStatus);
